import type { SearchResponse } from 'algoliasearch/lite'
import isEqual from 'lodash/isEqual'

import { isJSendError } from '@src/utils/jsend.util'

export type CachedSearchQuery = { indexName: string; params: Record<string, unknown> }

export type CachedSearchResult = SearchResponse

type CacheEntry = {
  query: CachedSearchQuery
  response?: CachedSearchResult
  promise?: Promise<CachedSearchResult>
}

function parseSearchResults(response: unknown): CachedSearchResult[] {
  if (isJSendError(response)) {
    throw new Error('message' in response ? response.message : 'Search request failed')
  }

  const results = (response as { results?: CachedSearchResult[] })?.results
  if (!Array.isArray(results)) {
    throw new Error('Search request failed')
  }

  return results
}

/**
 * Per-index-query cache for the federated `/search` custom search client.
 *
 * algoliasearch's built-in `responsesCache` keys on the WHOLE multi-query batch body.
 * Caching per individual `{ indexName, params }` query lets that transient batch be served
 * from the queries already fetched on previous tabs, costing no network request.
 *
 * Matching is by deep equality on `{ indexName, params }`, so any change to the query, filters,
 * facets or `hitsPerPage` produces a distinct entry. The cache lives for the lifetime of the
 * client instance it belongs to (one per mount).
 */
export class AlgoliaSearchCache {
  private entries: CacheEntry[] = []

  // Failed fetches aren't cached and propagate so react-instantsearch can show an error status.
  // fetcher is injected to keep the search server action's auth graph out of this module.
  async resolve(
    queries: CachedSearchQuery[],
    fetcher: (misses: CachedSearchQuery[]) => Promise<unknown>
  ): Promise<CachedSearchResult[]> {
    const misses: CachedSearchQuery[] = []
    const missSlots: number[] = []

    const slots = queries.map((query, index) => {
      const cached = this.get(query)
      if (cached) return cached

      misses.push(query)
      missSlots.push(index)

      return null
    })

    if (misses.length) {
      const fetched = Promise.resolve(fetcher(misses)).then(parseSearchResults)

      misses.forEach((query, missIndex) => {
        const result = fetched.then((results) => results[missIndex])
        this.setInFlight(query, result)
        slots[missSlots[missIndex]] = result
      })
    }

    return Promise.all(slots as Array<Promise<CachedSearchResult>>)
  }

  /** Returns null if any query is missing or still in flight. */
  getResolved(queries: CachedSearchQuery[]): CachedSearchResult[] | null {
    const results = queries.map((query) => {
      const entry = this.find(query)
      return entry?.response ?? null
    })

    return results.every(Boolean) ? (results as CachedSearchResult[]) : null
  }

  private find(query: CachedSearchQuery) {
    return this.entries.find((entry) => isEqual(entry.query, query))
  }

  private get(query: CachedSearchQuery): Promise<CachedSearchResult> | null {
    const entry = this.find(query)

    if (entry?.response) return Promise.resolve(entry.response)
    if (entry?.promise) return entry.promise

    return null
  }

  /**
   * Register an in-flight fetch for a query. The entry is promoted to a stored response once the
   * promise resolves, and dropped if it rejects so the next identical query retries.
   */
  private setInFlight(query: CachedSearchQuery, promise: Promise<CachedSearchResult>) {
    const entry: CacheEntry = { query, promise }
    this.entries.push(entry)

    promise.then(
      (response) => {
        entry.response = response
        entry.promise = undefined
      },
      () => {
        this.entries = this.entries.filter((current) => current !== entry)
      }
    )
  }
}
